import {
  GridApi,
  FilterModel,
  TextFilterModel,
  NumberFilterModel,
  DateFilterModel,
  SetFilterModel,
  ICombinedSimpleModel,
  ISimpleFilterModel,
} from '@ag-grid-community/core';
import { apiClient } from '@api/apiClient';
import { fetchSavedSearches } from '@store/loadboard/services';
import { SavedSearchCategory, SavedSearchT } from '@store/loadboard/types';
import { userStore } from '@store/user';
import { createError } from '@utils/errorUtils';
import { logError } from '@utils/errorLogging';
import { BillingQueueTabCache } from '@views/billingQueues/components/types';
import { debounce } from 'lodash';

import { billingQueueStore, setBillingQueueStore } from './store';
import { BillingQueueTabKey, BILLING_QUEUE_TAB_KEYS } from './types';

type SingleFilterModel =
  | TextFilterModel
  | NumberFilterModel
  | DateFilterModel
  | SetFilterModel;

type CombinedFilterModel = ICombinedSimpleModel<ISimpleFilterModel>;

const isCombinedFilter = (
  filter: SingleFilterModel | CombinedFilterModel,
): filter is CombinedFilterModel => {
  return 'operator' in filter;
};

/**
 * Guard flag to prevent infinite loops when applying cached state.
 * When true, grid event listeners will not trigger saves.
 */
let isApplyingCache = false;

/**
 * Get the cache name for a specific tab
 */
export const getBillingQueueCacheName = (
  tabKey: BillingQueueTabKey,
): string => {
  return `BillingQueue_${tabKey}`;
};

/**
 * Save the grid state for a specific tab to the server
 */
export const saveBillingQueueTabCache = async (
  tabKey: BillingQueueTabKey,
  gridApi: GridApi,
): Promise<void> => {
  try {
    const gridState = gridApi.getState();
    const cacheData: BillingQueueTabCache = {
      gridState,
    };

    await apiClient.post('/user/SaveSearchSettings', {
      userId: userStore.user.id,
      Category: SavedSearchCategory.BillingQueue,
      Name: getBillingQueueCacheName(tabKey),
      Params: JSON.stringify(cacheData),
    });

    // Also update local store cache
    setBillingQueueStore('tabCaches', tabKey, gridState);
  } catch (e) {
    const error = createError(e, 'Failed to save billing queue cache');
    logError('saveBillingQueueTabCache', error);
  }
};

/**
 * Create a debounced version of saveBillingQueueTabCache for a specific tab
 */
export const createDebouncedSaveCache = (tabKey: BillingQueueTabKey) => {
  return debounce((gridApi: GridApi) => {
    void saveBillingQueueTabCache(tabKey, gridApi);
  }, 500);
};

/**
 * Fetch all billing queue caches from the server
 */
export const fetchAllBillingQueueCaches = async (): Promise<void> => {
  try {
    const savedSearches = await fetchSavedSearches();
    const billingQueueCaches = savedSearches.filter(
      (s: SavedSearchT) => s.category === SavedSearchCategory.BillingQueue,
    );

    // Parse and store each tab's cache
    for (const cache of billingQueueCaches) {
      const tabKey = cache.name.replace(
        'BillingQueue_',
        '',
      ) as BillingQueueTabKey;
      if (BILLING_QUEUE_TAB_KEYS.includes(tabKey)) {
        try {
          const cacheData = JSON.parse(cache.params) as BillingQueueTabCache;
          setBillingQueueStore('tabCaches', tabKey, cacheData.gridState);
        } catch (e) {
          const error = createError(
            e,
            `Failed to parse cache for tab ${tabKey}`,
          );
          logError('fetchAllBillingQueueCaches', error);
        }
      }
    }
  } catch (e) {
    const error = createError(e, 'Failed to fetch billing queue caches');
    logError('fetchAllBillingQueueCaches', error);
  }
};

/**
 * Apply cached grid state to a grid
 */
export const applyBillingQueueCache = (
  tabKey: BillingQueueTabKey,
  gridApi: GridApi,
): void => {
  const cachedState = billingQueueStore.tabCaches[tabKey];
  if (!cachedState) {
    return;
  }

  try {
    // Set guard flag to prevent event listeners from triggering saves
    isApplyingCache = true;

    // Apply filter model
    const filterModel = cachedState.filter?.filterModel;
    if (filterModel) {
      gridApi.setFilterModel(filterModel);
    }

    // Apply sort model
    const sortModel = cachedState.sort?.sortModel;
    if (sortModel && sortModel.length > 0) {
      gridApi.applyColumnState({ state: sortModel });
    }

    // Apply column sizing
    const columnSizingModel = cachedState.columnSizing?.columnSizingModel;
    if (columnSizingModel && columnSizingModel.length > 0) {
      gridApi.setColumnWidths(
        columnSizingModel.map((size) => ({
          key: size.colId,
          newWidth: size.width ?? 100,
        })),
      );
    }

    // Apply column visibility
    const columnVisibility = cachedState.columnVisibility;
    if (columnVisibility) {
      if (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        columnVisibility.hiddenColIds &&
        columnVisibility.hiddenColIds.length > 0
      ) {
        gridApi.setColumnsVisible(columnVisibility.hiddenColIds, false);
      }
    }
  } catch (e) {
    const error = createError(e, `Failed to apply cache for tab ${tabKey}`);
    logError('applyBillingQueueCache', error);
  } finally {
    // Reset guard flag after applying cache
    isApplyingCache = false;
  }
};

/**
 * Setup grid event listeners for auto-saving filter/sort/column state
 */
export const setupBillingQueueGridListeners = (
  tabKey: BillingQueueTabKey,
  gridApi: GridApi,
): void => {
  const debouncedSave = createDebouncedSaveCache(tabKey);

  gridApi.addEventListener('filterChanged', () => {
    if (!isApplyingCache) {
      debouncedSave(gridApi);
    }
  });

  gridApi.addEventListener('sortChanged', () => {
    if (!isApplyingCache) {
      debouncedSave(gridApi);
    }
  });

  gridApi.addEventListener('columnResized', () => {
    if (!isApplyingCache) {
      debouncedSave(gridApi);
    }
  });

  gridApi.addEventListener('columnMoved', () => {
    if (!isApplyingCache) {
      debouncedSave(gridApi);
    }
  });

  gridApi.addEventListener('columnVisible', () => {
    if (!isApplyingCache) {
      debouncedSave(gridApi);
    }
  });
};

/**
 * Get the tab key for a given tab index
 */
export const getTabKeyFromIndex = (index: number): BillingQueueTabKey => {
  return BILLING_QUEUE_TAB_KEYS[index] ?? 'UploadQueue';
};

/**
 * Get the tab index for a given tab key
 */
export const getTabIndexFromKey = (tabKey: BillingQueueTabKey): number => {
  return BILLING_QUEUE_TAB_KEYS.indexOf(tabKey);
};

/**
 * Match value against a set filter
 */
const matchSetFilter = (value: unknown, filter: SetFilterModel): boolean => {
  const values = filter.values;
  if (values.length === 0) {
    return true;
  }

  // AG Grid uses null in the values array to represent blank/null/undefined/empty values
  const isBlankValue = value === null || value === undefined || value === '';

  return values.some((filterValue) => {
    if (filterValue === null) {
      return isBlankValue;
    }
    if (isBlankValue) {
      return false;
    }
    return String(filterValue) === String(value);
  });
};

/**
 * Match value against a text filter
 */
// eslint-disable-next-line complexity
const matchTextFilter = (value: unknown, filter: TextFilterModel): boolean => {
  const filterValue = String(filter.filter ?? '').toLowerCase();
  const cellValue = String(value ?? '').toLowerCase();

  switch (filter.type) {
    case 'contains':
      return cellValue.includes(filterValue);
    case 'notContains':
      return !cellValue.includes(filterValue);
    case 'equals':
      return cellValue === filterValue;
    case 'notEqual':
      return cellValue !== filterValue;
    case 'startsWith':
      return cellValue.startsWith(filterValue);
    case 'endsWith':
      return cellValue.endsWith(filterValue);
    case 'blank':
      return !value || value === '';
    case 'notBlank':
      return Boolean(value) && value !== '';
    default:
      return true;
  }
};

/**
 * Match value against a number filter
 */
const matchNumberFilter = (
  value: unknown,
  filter: NumberFilterModel,
): boolean => {
  if (filter.type === 'blank') {
    return value === null || value === undefined || isNaN(Number(value));
  }
  if (filter.type === 'notBlank') {
    return value !== null && value !== undefined && !isNaN(Number(value));
  }

  const filterValue = Number(filter.filter);
  const cellValue = Number(value);

  if (isNaN(cellValue)) return false;

  switch (filter.type) {
    case 'equals':
      return cellValue === filterValue;
    case 'notEqual':
      return cellValue !== filterValue;
    case 'lessThan':
      return cellValue < filterValue;
    case 'lessThanOrEqual':
      return cellValue <= filterValue;
    case 'greaterThan':
      return cellValue > filterValue;
    case 'greaterThanOrEqual':
      return cellValue >= filterValue;
    case 'inRange': {
      const filterTo = Number(filter.filterTo);
      return cellValue >= filterValue && cellValue <= filterTo;
    }
    default:
      return true;
  }
};

/**
 * Match value against a date filter
 */
const matchDateFilter = (value: unknown, filter: DateFilterModel): boolean => {
  if (filter.type === 'blank') {
    return !value || isNaN(new Date(value as string | number | Date).getTime());
  }
  if (filter.type === 'notBlank') {
    return (
      Boolean(value) &&
      !isNaN(new Date(value as string | number | Date).getTime())
    );
  }

  const cellDate = new Date(value as string | number | Date);
  const filterDate = new Date(filter.dateFrom as string);

  if (isNaN(cellDate.getTime())) return false;

  const cellTime = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  ).getTime();
  const filterTime = new Date(
    filterDate.getFullYear(),
    filterDate.getMonth(),
    filterDate.getDate(),
  ).getTime();

  switch (filter.type) {
    case 'equals':
      return cellTime === filterTime;
    case 'notEqual':
      return cellTime !== filterTime;
    case 'lessThan':
      return cellTime < filterTime;
    case 'greaterThan':
      return cellTime > filterTime;
    case 'inRange': {
      const filterToDate = new Date(filter.dateTo as string);
      const filterToTime = new Date(
        filterToDate.getFullYear(),
        filterToDate.getMonth(),
        filterToDate.getDate(),
      ).getTime();
      return cellTime >= filterTime && cellTime <= filterToTime;
    }
    default:
      return true;
  }
};

// Check if a record matches a single filter condition
const matchesSingleFilter = (
  value: unknown,
  filter: SingleFilterModel,
): boolean => {
  switch (filter.filterType) {
    case 'set':
      return matchSetFilter(value, filter as SetFilterModel);
    case 'text':
      return matchTextFilter(value, filter as TextFilterModel);
    case 'number':
      return matchNumberFilter(value, filter as NumberFilterModel);
    case 'date':
      return matchDateFilter(value, filter as DateFilterModel);
    default:
      return true;
  }
};

/**
 * Check if a record passes the current grid filter model.
 * This is used to determine if a socket update should be added to the grid.
 */
export const doesRecordMatchFilter = <T extends Record<string, unknown>>(
  record: T,
  filterModel: FilterModel | null,
  accountingGroupFilterIds?: number,
): boolean => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return true;
  }

  if (accountingGroupFilterIds !== undefined) {
    if (record['accountingGroupId'] != accountingGroupFilterIds) {
      return false;
    }
  }

  // Check each filter in the filter model
  for (const [field, filter] of Object.entries(filterModel)) {
    const value = record[field];
    const typedFilter = filter as SingleFilterModel | CombinedFilterModel;

    if (isCombinedFilter(typedFilter)) {
      const conditions = [
        typedFilter.condition1 as SingleFilterModel,
        typedFilter.condition2 as SingleFilterModel,
      ];

      if (typedFilter.operator === 'AND') {
        if (!conditions.every((cond) => matchesSingleFilter(value, cond))) {
          return false;
        }
      } else {
        if (!conditions.some((cond) => matchesSingleFilter(value, cond))) {
          return false;
        }
      }
    } else {
      if (!matchesSingleFilter(value, typedFilter)) {
        return false;
      }
    }
  }

  return true;
};
